import { VERTECS } from '@/data/vertex';
import { ICanvasElement } from '@/interface/canvas';
import { Results } from '@mediapipe/face_mesh';
import { Group } from 'three';
import * as THREE from 'three';

class AvatarManager {
  private static instance: AvatarManager = new AvatarManager();
  private targetQuaternion = new THREE.Quaternion();
  private currentQuaternion = new THREE.Quaternion();

  private mask!: Group;
  private height!: number;
  private width!: number;
  private scaleX!: number;
  private scaleY!: number;
  private offsetX!: number;
  private offsetY!: number;
  private smoothedVertices: number[] = []; // Store smoothed positions
  private readonly SMOOTHING_FACTOR = 0.3;
  isModelLoaded!: boolean;
  private facePosition = { x: 0, y: 0, z: 0 };
  private faceRotation = { x: 0, y: 0, z: 0 };
  webstageEntity: any;

  private stageEntitySmoothedPosition = { x: 0, y: 0, z: 0 };
  private readonly STAGE_ENTITY_POSITION_SMOOTHING = 0.25;

  private readonly ROTATION_SMOOTHING = 0.15;
  private readonly SCALE_SMOOTHING = 0.2;
  private smoothedScale = 1.0;

  static getInstance(): AvatarManager {
    return AvatarManager.instance;
  }

  async loadFaceV2(faceConfig: ICanvasElement, height: number, width: number) {
    this.height = height;
    this.width = width;

    const cameraDistance = 10; // z-position of camera
    const fov = 80; // degrees
    const aspect = width / height;

    const fovRadians = (fov * Math.PI) / 180;
    const visibleHeight = 2 * cameraDistance * Math.tan(fovRadians / 2);
    const visibleWidth = visibleHeight * aspect;

    const faceEntity = document.createElement('a-entity');
    faceEntity.setAttribute(
      'fbx-model',
      'src: url(https://rhym.s3.ap-south-1.amazonaws.com/AR/assets/canonical_face_model.fbx)'
    );

    faceEntity.setAttribute(
      'face-morph',
      `src:${faceConfig?.uri}; roughness: 1; metalness: 0; color: ${
        faceConfig?.color
      }; opacity: ${faceConfig?.opacity || 1};`
    );
    faceEntity.setAttribute('scale', `${visibleWidth} ${visibleHeight} 1`);
    faceEntity.setAttribute('rotation', '180 0 0');
    const scene = document.getElementById('scene');
    if (scene) scene.appendChild(faceEntity);

    this.isModelLoaded = true;
    const video: any = document.getElementById('tryon_face_webcam');

    const { scaleX, scaleY, offsetX, offsetY } = this.getVideoScale(video);
    this.scaleX = scaleX;
    this.scaleY = scaleY;
    this.offsetX = offsetX;
    this.offsetY = offsetY;
    this.webstageEntity = document.getElementById('entity_elements');
  }
  updateFacialTransforms(results: Results) {
    if (!results || !this.isModelLoaded) return;
    this.updateTranslation(results);
  }

  updateTranslation(results: Results) {
    if (!results.multiFaceLandmarks) return;

    // const maskObject: any = this.mask.getObjectByName('mask');

    try {
      const multiFaceLandmarks = results.multiFaceLandmarks;

      for (const index in multiFaceLandmarks) {
        const faceLandmark = multiFaceLandmarks[index];
        const vertices = [];

        for (const index in VERTECS) {
          const landmarksIndex = VERTECS[index];
          const newVec = faceLandmark[landmarksIndex - 1];
          // vertices.push(newVec.x - 0.5, newVec.y - 0.5, newVec.z);
          const adjustedX = (newVec.x - this.offsetX) / this.scaleX - 0.5;
          const adjustedY = (newVec.y - this.offsetY) / this.scaleY - 0.5;
          vertices.push(adjustedX, adjustedY, newVec.z);
          // xCoordinates.push(newVec.x);
          // yCoordinates.push(newVec.y);
        }
        const smoothedVertices = this.smoothLandmarks(vertices);
        const verticesArray = new Float32Array(smoothedVertices);

        const aframeEntity: any = document.querySelector('[face-morph]');
        if (!aframeEntity?.components?.['face-morph']) return;
        aframeEntity?.components?.['face-morph'].updatePosition(verticesArray);
        // maskObject.children[0].scale.set(1 + faceWidth, 1 + faceHeight, 1);

        // maskObject.scale.needsUpdate = true;
      }
      this.updateEntity(results);
    } catch (e) {
      //
    }
  }

  private updateEntity(results: Results) {
    const faceLandmarks = results.multiFaceLandmarks[0];
    if (faceLandmarks) {
      const transform = this.getFaceTransform(faceLandmarks);
      this.facePosition = transform.position;
      this.faceRotation = transform.rotation;

      this.stageEntitySmoothedPosition.x +=
        (transform.position.x - this.stageEntitySmoothedPosition.x) *
        this.STAGE_ENTITY_POSITION_SMOOTHING;
      this.stageEntitySmoothedPosition.y +=
        (transform.position.y - this.stageEntitySmoothedPosition.y) *
        this.STAGE_ENTITY_POSITION_SMOOTHING;
      this.stageEntitySmoothedPosition.z +=
        (transform.position.z - this.stageEntitySmoothedPosition.z) *
        this.STAGE_ENTITY_POSITION_SMOOTHING;

      this.smoothedScale +=
        (transform.scale - this.smoothedScale) * this.SCALE_SMOOTHING;

      // Slerp rotation
      this.targetQuaternion.setFromEuler(
        new THREE.Euler(
          transform.rotation.x,
          transform.rotation.y,
          transform.rotation.z
        )
      );
      this.currentQuaternion.slerp(
        this.targetQuaternion,
        this.ROTATION_SMOOTHING
      );

      const euler = new THREE.Euler().setFromQuaternion(this.currentQuaternion);
      this.facePosition = this.stageEntitySmoothedPosition;
      this.faceRotation = { x: euler.x, y: euler.y, z: euler.z };
      if (this.webstageEntity) {
        this.webstageEntity.setAttribute(
          'position',
          `${this.stageEntitySmoothedPosition.x} ${this.stageEntitySmoothedPosition.y} ${this.stageEntitySmoothedPosition.z}`
        );

        this.webstageEntity.setAttribute('rotation', {
          x: (this.faceRotation.x * 180) / Math.PI,
          y: (-this.faceRotation.y * 180) / Math.PI,
          z: (-this.faceRotation.z * 180) / Math.PI,
        });
        this.webstageEntity.setAttribute('scale', {
          x: -this.smoothedScale,
          y: -this.smoothedScale,
          z: this.smoothedScale,
        });
      }
    }
  }

  private getFaceScale(faceLandmarks: any): number {
    // Get landmarks
    const leftEye = faceLandmarks[33]; // Left eye outer corner
    const rightEye = faceLandmarks[263]; // Right eye outer corner

    // Convert normalized coordinates (0-1) to pixel coordinates
    const leftEyePixel = {
      x: leftEye.x * this.width,
      y: leftEye.y * this.height,
    };

    const rightEyePixel = {
      x: rightEye.x * this.width,
      y: rightEye.y * this.height,
    };

    // Calculate eye distance in pixels
    const eyeDistancePixels = Math.sqrt(
      Math.pow(rightEyePixel.x - leftEyePixel.x, 2) +
        Math.pow(rightEyePixel.y - leftEyePixel.y, 2)
    );

    // Reference eye distance in pixels (average face at neutral distance)
    // Typical eye distance is ~60-100 pixels depending on your video resolution
    const referenceEyeDistance = 20; // Adjust based on your setup

    // Calculate scale ratio
    const scale = eyeDistancePixels / referenceEyeDistance;

    return scale;
  }

  private getFaceTransform(faceLandmarks: any) {
    const noseBridge = faceLandmarks[168];
    const noseTip = faceLandmarks[1];
    const chin = faceLandmarks[152];
    const leftEye = faceLandmarks[33];
    const rightEye = faceLandmarks[263];

    const noseX = 1 - noseBridge.x;

    // Step 2: Apply video scaling corrections
    const adjustedX = (noseX - this.offsetX) / this.scaleX;
    const adjustedY = (noseBridge.y - this.offsetY) / this.scaleY;

    // Step 3: Convert to centered normalized coordinates (-0.5 to 0.5)
    const normalizedX = adjustedX - 0.5;
    const normalizedY = adjustedY - 0.5;

    // Step 4: Convert to world space based on camera FOV and distance
    const cameraDistance = 5; // Your face entity z-position relative to camera
    const fov = 100; // degrees
    const aspect = this.width / this.height;

    const fovRadians = (fov * Math.PI) / 180;
    const visibleHeight = 2 * cameraDistance * Math.tan(fovRadians / 2);
    const visibleWidth = visibleHeight * aspect;

    // Scale normalized coordinates to world space
    const worldX = normalizedX * visibleWidth;
    const worldY = -normalizedY * visibleHeight; // Flip Y (MediaPipe Y goes down)

    // Z coordinate: MediaPipe z is relative to face width, scale it
    const worldZ = noseBridge.z * visibleWidth;

    // Calculate rotations
    const yaw = Math.atan2(noseTip.x - chin.x, noseTip.y - chin.y);
    const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x);
    const pitch = Math.atan2(noseTip.z - chin.z, noseTip.y - chin.y);

    return {
      position: { x: worldX, y: worldY, z: worldZ },
      rotation: { x: pitch, y: yaw, z: roll },
      scale: this.getFaceScale(faceLandmarks),
    };
  }

  private smoothLandmarks(newVertices: number[]): number[] {
    if (this.smoothedVertices.length === 0) {
      // Initialize on first run
      this.smoothedVertices = [...newVertices];
      return newVertices;
    }

    const smoothed: number[] = [];
    for (let i = 0; i < newVertices.length; i++) {
      // Exponential moving average: smoothed = alpha * new + (1 - alpha) * previous
      smoothed[i] =
        this.SMOOTHING_FACTOR * newVertices[i] +
        (1 - this.SMOOTHING_FACTOR) * this.smoothedVertices[i];
    }

    this.smoothedVertices = smoothed;
    return smoothed;
  }

  getVideoScale(videoElement?: HTMLVideoElement) {
    let scaleX = 1;
    let scaleY = 1;
    let offsetX = 0;
    let offsetY = 0;
    if (!videoElement) return { scaleX, scaleY, offsetX, offsetY };
    const containerWidth = videoElement.offsetWidth;
    const containerHeight = videoElement.offsetHeight;
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    const containerAspect = containerWidth / containerHeight;
    const videoAspect = videoWidth / videoHeight;

    if (containerAspect > videoAspect) {
      // Container is wider - video fills width, crops top/bottom
      scaleX = 1;
      scaleY = videoAspect / containerAspect;
      offsetY = (1 - scaleY) / 2;
    } else {
      // Container is taller - video fills height, crops left/right
      scaleY = 1;
      scaleX = containerAspect / videoAspect;
      offsetX = (1 - scaleX) / 2;
    }

    return { scaleX, scaleY, offsetX, offsetY };
  }
}

export default AvatarManager;
